import * as React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, DollarSign, Plus, Loader2, Trash2, Pencil, PrinterCheck, IndianRupeeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useGetAllCourseQuery,
  useDeleteCourseMutation,
} from '@/store/courseApi';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import { CustomAlertDialog } from './AlertDialog';

interface Course {
  _id: string;
  course: string;
  cDuration: string;
  cFee: number;
  createdAt: string;
}

const CourseCard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const { data: courses = [], isLoading } = useGetAllCourseQuery();
  // console.log("courses: ",courses);
  const [DeleteCourse, { isSuccess: deleteSuccess }] =
    useDeleteCourseMutation();
  const [courseList, setCourseList] = React.useState<Course[]>([]);

  React.useEffect(() => {
    setCourseList(courses); // Initialize state when data is fetched
  }, [courses]);

  const handleDeleteCourse = (id: string) => {
    DeleteCourse({
      courseId: id,
      Token: user?.token,
    });
  };

  const colors = [
    'bg-blue-100',
    'bg-teal-100',
    'bg-purple-100',
    'bg-orange-100',
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-foreground">Featured Courses</h1>
        {user?.role === 'admin' && (
          <Link to="/add-course">
            <div className="btn h-10 w-10 flex items-center justify-center text-white rounded-3xl bg-[#26BDB0]">
              <Plus />
            </div>
          </Link>
        )}
      </div>

      {isLoading && <Loader2 className="animate-spin text-center mx-auto" />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {courseList.map((course, idx: number) => (
          <Card
            key={course._id}
            className={`transform transition-all duration-300 hover:scale-105 ${
              colors[idx % colors.length]
            }`}
          >
            <CardContent className="p-6 bg-teal-100/70">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-semibold flex-1">
                  {course.course}
                </h2>
                <Badge variant="secondary" className="text-lg">
                  #{idx + 1}
                </Badge>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-5 w-5 text-teal-600" />
                  <span className="text-lg">
                    Fee: ₹{course.cFee.toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <span className="text-lg">Duration: {course.cDuration}</span>
                </div>

                <div className="flex justify-between items-center pt-4">
                  <Link to={encodeURIComponent(course.course.trim())}>
                    <Badge className="px-4 py-2 text-sm bg-[#26BDB0] hover:bg-[#49ccc1e2]">View Students</Badge>
                  </Link>

                  {user?.role === 'admin' && (
                    <span className="flex gap-4">
                      {/* Using confirmation dialog */}
                      <CustomAlertDialog
                        triggerButton={
                          <Trash2 className="h-5 w-5 text-red-500 cursor-pointer" />
                        }
                        title="Delete Course"
                        description={`Are you sure you want to delete the course ${course.course}?`}
                        onConfirm={() => handleDeleteCourse(course._id)}
                      />
                      <Link to={`/update-course/${course._id}`}>
                        <Pencil className="h-5 w-5 text-slate-500 cursor-pointer" />
                      </Link>
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CourseCard;
